import {
  CAlert,
  CButton,
  CCloseButton,
  CCol,
  COffcanvas,
  COffcanvasBody,
  COffcanvasHeader,
  COffcanvasTitle,
  CForm,
  CFormFeedback,
  CFormInput,
  CFormLabel,
  CLoadingButton,
} from '@coreui/react-pro'
import React, { useRef, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import GamePrizeService from 'src/service/GamePrizeService'
import ToastComponent from 'src/components/common/TaostComponent'
import ImageDropzone from 'src/components/common/ImageDropzone'
import useSponsorQuillModules from '../Sponsor/useSponsorQuillModules'

const SUPPORTED_FORMATS = ['image/jpg', 'image/png', 'image/jpeg', 'image/gif']
const ACCEPTED_IMAGE_TYPES = 'image/jpeg,image/png,image/gif'
const MAX_IMAGE_SIZE = 1024 * 1024 // 1MB

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Name is required').max(50, '50 Character Limit is allowed.'),
  image: Yup.mixed()
    .nullable()
    .required('Prize image is required')
    .test('size', 'File size too large, max file size is 1MB', (value) =>
      value ? value.size <= MAX_IMAGE_SIZE : true,
    )
    .test(
      'type',
      'Invalid file format selection',
      (value) => !value || SUPPORTED_FORMATS.includes(value?.type),
    ),
})

const AddForm = ({ visible, onClose, fetchUsers }) => {
  const [loader, setLoader] = useState(false)
  const [errorMessages, setErrorMessages] = useState([])
  const keepOpenRef = useRef(false)
  const quillRef = useRef()
  const modules = useSponsorQuillModules(quillRef)

  const formik = useFormik({
    initialValues: {
      name: '',
      prize_type: '',
      about: '',
      image: null,
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (data, actions) => {
      const formData = new FormData()
      formData.append('name', data.name)
      formData.append('prize_type', data.prize_type)
      formData.append('about', data.about)
      formData.append('image', data.image)

      setLoader(true)
      GamePrizeService.savePrize(formData)
        .then((res) => {
          if (res.success) {
            setErrorMessages([])
            fetchUsers?.()
            ToastComponent(res.message, 'success')
            resetFields(actions)
            if (!keepOpenRef.current) {
              onClose()
            }
          } else {
            const errors =
              res.errors && Array.isArray(res.errors)
                ? res.errors
                : [res.message || 'Something went wrong.']
            setErrorMessages(errors)
          }
        })
        .catch(() => {
          ToastComponent('Something went wrong. Please try again.', 'error')
        })
        .finally(() => setLoader(false))
    },
  })

  const resetFields = (actions) => {
    actions.resetForm({
      values: { name: '', prize_type: '', about: '', image: null },
    })
  }

  const handleClose = () => {
    resetFields(formik)
    setErrorMessages([])
    onClose()
  }

  const submitForm = (keepOpen) => {
    keepOpenRef.current = keepOpen
    formik.handleSubmit()
  }

  return (
    <COffcanvas
      placement="end"
      visible={visible}
      onHide={handleClose}
      backdrop
      style={{ width: 560 }}
    >
      <COffcanvasHeader>
        <div>
          <COffcanvasTitle>Add Prize</COffcanvasTitle>
          <div className="text-medium-emphasis small">
            Create a prize members can win, with its type, image and description.
          </div>
        </div>
        <CCloseButton className="text-reset" onClick={handleClose} />
      </COffcanvasHeader>
      <COffcanvasBody className="d-flex flex-column">
        {errorMessages.length > 0 && (
          <div className="error-list">
            {errorMessages.map((error, index) => (
              <CAlert color="danger" key={index}>
                {error}
              </CAlert>
            ))}
          </div>
        )}
        <CForm className="row g-3 flex-grow-1 align-content-start" onSubmit={formik.handleSubmit}>
          <CCol xs={12}>
            <CFormLabel htmlFor="name">Prize Name *</CFormLabel>
            <CFormInput
              id="name"
              name="name"
              placeholder="Prize Name"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              invalid={!!(formik.errors.name && formik.touched.name)}
            />
            {formik.errors.name && formik.touched.name && (
              <CFormFeedback invalid>{formik.errors.name}</CFormFeedback>
            )}
          </CCol>
          <CCol xs={12}>
            <CFormLabel htmlFor="prize_type">Prize Type</CFormLabel>
            <CFormInput
              id="prize_type"
              name="prize_type"
              placeholder="Enter Prize Type"
              value={formik.values.prize_type}
              onChange={formik.handleChange}
            />
          </CCol>
          <CCol xs={12}>
            <CFormLabel htmlFor="image">Prize Image *</CFormLabel>
            <ImageDropzone
              accept={ACCEPTED_IMAGE_TYPES}
              hint="JPG, PNG or GIF up to 1MB."
              minHeight={140}
              file={formik.values.image}
              error={formik.touched.image ? formik.errors.image : null}
              onChange={(selected) => {
                formik.setTouched({ ...formik.touched, image: true })
                formik.setFieldValue('image', selected)
              }}
            />
          </CCol>
          <CCol xs={12}>
            <CFormLabel htmlFor="about">Prize Description</CFormLabel>
            <div className="gp-editor">
              <ReactQuill
                theme="snow"
                ref={quillRef}
                value={formik.values.about}
                onChange={(content) => formik.setFieldValue('about', content)}
                modules={modules}
              />
            </div>
          </CCol>
        </CForm>
      </COffcanvasBody>
      <div className="d-flex justify-content-between gap-2 p-3 border-top">
        <CButton color="secondary" variant="outline" onClick={handleClose}>
          Cancel
        </CButton>
        <div className="d-flex gap-2">
          <CLoadingButton
            color="secondary"
            variant="outline"
            loading={loader}
            onClick={() => submitForm(true)}
          >
            Save &amp; Add Another
          </CLoadingButton>
          <CLoadingButton color="primary" loading={loader} onClick={() => submitForm(false)}>
            Create Prize
          </CLoadingButton>
        </div>
      </div>
    </COffcanvas>
  )
}

export default AddForm
